import { Link, useParams } from "react-router-dom";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { useGetOrderInvoiceQuery } from "../slices/ordersApiSlice";

const formatMoney = (amount, currency) => {
  const numeric = Number(amount || 0);
  if (Number.isNaN(numeric)) return `${currency} 0.00`;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(numeric);
  } catch {
    return `${currency || "USD"} ${numeric.toFixed(2)}`;
  }
};

function InvoiceScreen() {
  const { id: orderId } = useParams();
  const { data: invoice, isLoading, error } = useGetOrderInvoiceQuery(orderId);

  if (isLoading) return <Loader />;
  if (error) {
    return <Message variant="danger">{error?.data?.message || error.error}</Message>;
  }

  if (!invoice) return null;

  return (
    <div className="printable-invoice space-y-4">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
            Invoice
          </p>
          <h1 className="text-3xl font-semibold text-slate-900">{invoice.invoiceId}</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Print Invoice
          </button>
          <Link
            to="/purchases"
            className="inline-flex items-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white no-underline hover:bg-sky-700"
          >
            Back to Purchases
          </Link>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm print:rounded-none print:border-0 print:px-4 print:pb-2 print:pt-4 print:shadow-none">
        <div className="hidden border-b border-slate-200 pb-4 print:mb-5 print:block">
          <div className="flex items-start justify-between gap-6">
            <div className="space-y-1">
              <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Practidex Marketplace
              </p>
              <h1 className="m-0 text-[24px] font-semibold text-slate-900">Invoice</h1>
              <p className="m-0 text-[13px] text-slate-600">{invoice.invoiceId}</p>
            </div>
            <div className="max-w-[240px] text-right text-[12px] leading-6 text-slate-600">
              <p className="m-0">Digital marketplace invoice for licensed educational resources.</p>
            </div>
          </div>
        </div>

        <div className="invoice-print-header grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <div className="invoice-print-block pr-0 md:pr-6 print:pr-8">
            <h2 className="text-lg font-semibold text-slate-900 print:text-[16px]">Billing Details</h2>
            <div className="mt-3 space-y-1 text-sm leading-6 text-slate-700 print:mt-4 print:space-y-2 print:text-[13px]">
              <p className="m-0"><strong>Name:</strong> {invoice.buyer?.name}</p>
              <p className="m-0"><strong>Email:</strong> {invoice.buyer?.email}</p>
              <p className="m-0 break-words">
                <strong>Address:</strong> {invoice.buyer?.billingAddress?.address}, {invoice.buyer?.billingAddress?.city}{" "}
                {invoice.buyer?.billingAddress?.postalCode}, {invoice.buyer?.billingAddress?.country}
              </p>
            </div>
          </div>

          <div className="invoice-print-block space-y-1 text-sm leading-6 text-slate-700 md:pl-6 md:text-right print:pl-8 print:space-y-2 print:text-[13px]">
            <p className="m-0"><strong>Order ID:</strong> {invoice.orderId}</p>
            <p className="m-0"><strong>Invoice Date:</strong> {new Date(invoice.createdAt).toLocaleDateString("en-US")}</p>
            <p className="m-0"><strong>Paid At:</strong> {invoice.paidAt ? new Date(invoice.paidAt).toLocaleString("en-US") : "Pending"}</p>
            <p className="m-0"><strong>Payment Method:</strong> {invoice.payment?.method}</p>
            <p className="m-0 break-all"><strong>Payment Reference:</strong> {invoice.payment?.referenceId || "N/A"}</p>
            <p className="m-0"><strong>Status:</strong> {invoice.payment?.status}</p>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 print:mt-8 print:rounded-none">
          <table className="min-w-full border-collapse text-sm print:text-[13px]">
            <thead className="bg-slate-50 text-left text-slate-600 print:bg-transparent">
              <tr>
                <th className="px-4 py-3 font-semibold print:px-3 print:py-4">Item</th>
                <th className="px-4 py-3 font-semibold print:px-3 print:py-4">Licenses</th>
                <th className="px-4 py-3 font-semibold print:px-3 print:py-4">Unit Price</th>
                <th className="px-4 py-3 font-semibold print:px-3 print:py-4">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((item, index) => (
                <tr key={`${item.productId}-${index}`} className="border-t border-slate-100 text-slate-700 print:break-inside-avoid">
                  <td className="px-4 py-3 print:px-3 print:py-4">
                    <div>
                      <p className="m-0 font-medium text-slate-900">{item.name}</p>
                      {item.isFree && (
                        <span className="mt-1 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          Free item
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 print:px-3 print:py-4">{item.quantity}</td>
                  <td className="px-4 py-3 print:px-3 print:py-4">{item.isFree ? "Free" : formatMoney(item.unitAmount, invoice.currency)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900 print:px-3 print:py-4">
                    {item.isFree ? "Free" : formatMoney(item.lineAmount, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end print:mt-8">
          <div className="w-full max-w-xs rounded-2xl border border-slate-200 bg-slate-50 p-4 print:max-w-[280px] print:rounded-xl print:bg-white print:p-5">
            <div className="flex items-center justify-between text-sm text-slate-700 print:text-[13px]">
              <span>Subtotal</span>
              <strong>{formatMoney(invoice.subtotal, invoice.currency)}</strong>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 text-base font-semibold text-slate-900 print:mt-4 print:pt-4 print:text-[15px]">
              <span>Total Paid</span>
              <span>{formatMoney(invoice.totalPaid, invoice.currency)}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default InvoiceScreen;
